import { useState, useEffect, useRef } from "react";

const BG = "#000";
const SURFACE = "#141412";
const BORDER = "#242220";
const TEXT = "#edeae2";
const MUTED = "#8a8680";
const DIM = "#585450";
const GREEN = "#5ab870";
const GREEN_BG = "#0c1a0e";
const GREEN_BORDER = "#1e3d22";
const RED = "#e06060";
const RED_BG = "#1a0c0c";
const RED_BORDER = "#3d1e1e";

const inputSt = {
  width: "100%", maxWidth: "100%", padding: "13px 15px",
  background: "#111", border: `1px solid ${BORDER}`, borderRadius: 8,
  color: TEXT, fontSize: 15,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  boxSizing: "border-box", outline: "none", WebkitAppearance: "none",
};

const REGION_CURRENCY = {
  "New Zealand": "NZD", "Australia": "AUD", "United Kingdom": "GBP",
  "United States": "USD", "Canada": "CAD", "Europe": "EUR", "Other": "USD",
};

const REGIONS = [
  { label: "New Zealand", flag: "🇳🇿" }, { label: "Australia", flag: "🇦🇺" },
  { label: "United Kingdom", flag: "🇬🇧" }, { label: "United States", flag: "🇺🇸" },
  { label: "Canada", flag: "🇨🇦" }, { label: "Europe", flag: "🌍" },
  { label: "Other", flag: "🌐" },
];

const CURRENCIES = ["NZD", "AUD", "GBP", "USD", "CAD", "EUR", "Other"];

const PLATFORMS = ["Trade Me", "Facebook Marketplace", "eBay", "Reverb", "Gumtree", "Craigslist", "Other"];

const MUSICIAN_TYPES = [
  "Musician (gigging / performing)", "Hobbyist / plays for fun",
  "Collector", "Luthier / repair tech", "Student / learning", "Other",
];

const INSTRUMENTS = ["Guitar (acoustic)", "Guitar (electric)", "Bass", "Keys / Piano", "Other"];

const SETUP_EXP = [
  "Yes, confident doing full setups", "Yes, basic stuff only",
  "Not yet but want to learn", "No, I take it to a tech",
];

const GEAR_ACQ = ["Buy new", "Buy second-hand", "Both", "I flip / trade gear"];

const GOALS = [
  "Tracking my gear collection", "Evaluating gear before buying",
  "Flipping instruments", "Learning to do my own setups", "All of the above",
];

function Chip({ label, selected, onToggle, orange }) {
  return (
    <button
      onClick={onToggle}
      style={{
        padding: "10px 16px", borderRadius: 8, fontFamily: "inherit", fontSize: 14, cursor: "pointer",
        border: `1px solid ${selected ? orange : BORDER}`,
        background: selected ? `rgba(${orange === "#E8621A" ? "232,98,26" : "29,158,117"},0.1)` : "#0f0f0f",
        color: selected ? orange : MUTED,
        transition: "border-color 0.15s, color 0.15s",
      }}
    >{label}</button>
  );
}

function ProgressDots({ total, current, orange }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 32 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ width: i === current ? 20 : 8, height: 8, borderRadius: 4, background: i === current ? orange : i < current ? `rgba(${orange === "#E8621A" ? "232,98,26" : "29,158,117"},0.4)` : BORDER, transition: "width 0.2s, background 0.2s" }} />
      ))}
    </div>
  );
}

function SectionLabel({ children, orange }) {
  return <div style={{ fontSize: 11, color: orange || MUTED, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 10, fontWeight: 700 }}>{children}</div>;
}

export default function OnboardingFlow({ userId, orange, onComplete }) {
  const ORANGE = orange || "#E8621A";
  const [step, setStep] = useState(0); // 0=setup, 1=journey, 2=pwa

  // Screen 1 state
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken" | "error"
  const [usernameError, setUsernameError] = useState("");
  const [region, setRegion] = useState("");
  const [currency, setCurrency] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [step1Error, setStep1Error] = useState("");
  const [step1Saving, setStep1Saving] = useState(false);

  // Screen 2 state
  const [musicianType, setMusicianType] = useState("");
  const [instrumentsPlayed, setInstrumentsPlayed] = useState([]);
  const [setupExp, setSetupExp] = useState("");
  const [gearAcq, setGearAcq] = useState("");
  const [goals, setGoals] = useState([]);
  const [step2Saving, setStep2Saving] = useState(false);

  // PWA install
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone] = useState(() => window.matchMedia?.('(display-mode: standalone)').matches);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const checkUsername = async (u) => {
    const clean = u.trim().toLowerCase();
    if (!clean || clean.length < 3) { setUsernameStatus(null); return; }
    setUsernameStatus("checking");
    try {
      const r = await fetch(`/api/users/check-username?u=${encodeURIComponent(clean)}&exclude=${encodeURIComponent(userId)}`);
      const d = await r.json();
      if (d.error) { setUsernameStatus("error"); setUsernameError(d.error); }
      else { setUsernameStatus(d.available ? "available" : "taken"); setUsernameError(d.available ? "" : "Username already taken"); }
    } catch(e) { setUsernameStatus(null); }
  };

  const handleRegionSelect = (r) => {
    setRegion(r);
    setCurrency(REGION_CURRENCY[r] || "USD");
  };

  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const toggleInstrument = (i) => setInstrumentsPlayed(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  const toggleGoal = (g) => setGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);

  const handleStep1Next = async () => {
    if (!username.trim() || username.trim().length < 3) { setStep1Error("Username must be at least 3 characters."); return; }
    if (!/^[a-z0-9_]+$/i.test(username.trim())) { setStep1Error("Username can only contain letters, numbers, and underscores."); return; }
    if (!region) { setStep1Error("Please select your region."); return; }
    if (!platforms.length) { setStep1Error("Please select at least one platform."); return; }
    if (usernameStatus === "taken") { setStep1Error("That username is already taken."); return; }
    setStep1Error("");
    setStep(1);
  };

  const saveAndComplete = async (skipJourney = false) => {
    setStep2Saving(true);
    try {
      await fetch('/api/users/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          username: username.trim(),
          region,
          currency,
          platforms,
          musician_type: skipJourney ? null : musicianType || null,
          instruments_played: skipJourney ? null : instrumentsPlayed.length ? instrumentsPlayed : null,
          setup_experience: skipJourney ? null : setupExp || null,
          gear_acquisition: skipJourney ? null : gearAcq || null,
          strumiq_goals: skipJourney ? null : goals.length ? goals : null,
        }),
      });
      if (!isStandalone) {
        setStep(2);
      } else {
        onComplete({ username: username.trim(), region, currency, platforms });
      }
    } catch(e) {
      console.error('Onboarding save error:', e);
    } finally {
      setStep2Saving(false);
    }
  };

  const handlePWAInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    onComplete({ username: username.trim(), region, currency, platforms });
  };

  // ── Screen 1 ───────────────────────────────────────────────────────────────
  const screen1 = (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 24px 100px" }}>
      <ProgressDots total={3} current={0} orange={ORANGE} />
      <div style={{ fontSize: 26, fontWeight: 700, color: TEXT, marginBottom: 8 }}>Let's get you set up</div>
      <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 32 }}>This helps us give you accurate valuations and relevant market information.</div>

      {/* Username */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>Username / Handle</SectionLabel>
        <div style={{ fontSize: 12, color: DIM, marginBottom: 8 }}>Used across leaderboards, Mates Gear history, and social features.</div>
        <div style={{ position: "relative" }}>
          <input
            value={username}
            onChange={e => { setUsername(e.target.value); setUsernameStatus(null); setUsernameError(""); }}
            onBlur={() => checkUsername(username)}
            placeholder="e.g. bassmanNZ, GuitarJon"
            maxLength={20}
            style={{ ...inputSt, paddingRight: 40, borderColor: usernameStatus === "taken" ? RED : usernameStatus === "available" ? GREEN : BORDER }}
          />
          {usernameStatus === "checking" && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: DIM }}>…</div>}
          {usernameStatus === "available" && (
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          )}
          {usernameStatus === "taken" && (
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={RED} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          )}
        </div>
        {usernameError && <div style={{ fontSize: 12, color: RED, marginTop: 5 }}>{usernameError}</div>}
      </div>

      {/* Region */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>Region</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {REGIONS.map(r => (
            <button key={r.label} onClick={() => handleRegionSelect(r.label)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
              borderRadius: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              border: `1px solid ${region === r.label ? ORANGE : BORDER}`,
              background: region === r.label ? `rgba(${ORANGE === "#E8621A" ? "232,98,26" : "29,158,117"},0.08)` : SURFACE,
            }}>
              <span style={{ fontSize: 20 }}>{r.flag}</span>
              <span style={{ fontSize: 13, color: region === r.label ? ORANGE : TEXT, fontWeight: region === r.label ? 600 : 400 }}>{r.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Currency */}
      {region && (
        <div style={{ marginBottom: 24 }}>
          <SectionLabel orange={ORANGE}>Preferred Currency <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(auto-filled, override if needed)</span></SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {CURRENCIES.map(c => (
              <Chip key={c} label={c} selected={currency === c} onToggle={() => setCurrency(c)} orange={ORANGE} />
            ))}
          </div>
        </div>
      )}

      {/* Platforms */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>Primary Resale Platform(s)</SectionLabel>
        <div style={{ fontSize: 12, color: DIM, marginBottom: 8 }}>Where do you buy and sell gear?</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {PLATFORMS.map(p => (
            <Chip key={p} label={p} selected={platforms.includes(p)} onToggle={() => togglePlatform(p)} orange={ORANGE} />
          ))}
        </div>
      </div>

      {step1Error && <div style={{ padding: "10px 14px", background: RED_BG, border: `1px solid ${RED_BORDER}`, borderRadius: 8, fontSize: 13, color: RED, marginBottom: 14 }}>{step1Error}</div>}

      <button
        onClick={handleStep1Next}
        disabled={step1Saving}
        style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: ORANGE, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 15, cursor: "pointer" }}
      >
        Next →
      </button>
    </div>
  );

  // ── Screen 2 ───────────────────────────────────────────────────────────────
  const screen2 = (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 24px 100px" }}>
      <ProgressDots total={3} current={1} orange={ORANGE} />
      <div style={{ fontSize: 26, fontWeight: 700, color: TEXT, marginBottom: 8 }}>Tell us about yourself</div>
      <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 32 }}>Helps us tailor StrumIQ to how you use it. You can skip this and fill it in later.</div>

      {/* Q1 */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>How would you describe yourself?</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {MUSICIAN_TYPES.map(m => (
            <Chip key={m} label={m} selected={musicianType === m} onToggle={() => setMusicianType(p => p === m ? "" : m)} orange={ORANGE} />
          ))}
        </div>
      </div>

      {/* Q2 */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>What instruments do you play or work on?</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {INSTRUMENTS.map(i => (
            <Chip key={i} label={i} selected={instrumentsPlayed.includes(i)} onToggle={() => toggleInstrument(i)} orange={ORANGE} />
          ))}
        </div>
      </div>

      {/* Q3 */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>Do you currently do your own setups?</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SETUP_EXP.map(s => (
            <Chip key={s} label={s} selected={setupExp === s} onToggle={() => setSetupExp(p => p === s ? "" : s)} orange={ORANGE} />
          ))}
        </div>
      </div>

      {/* Q4 */}
      <div style={{ marginBottom: 24 }}>
        <SectionLabel orange={ORANGE}>How do you mainly acquire gear?</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {GEAR_ACQ.map(g => (
            <Chip key={g} label={g} selected={gearAcq === g} onToggle={() => setGearAcq(p => p === g ? "" : g)} orange={ORANGE} />
          ))}
        </div>
      </div>

      {/* Q5 */}
      <div style={{ marginBottom: 32 }}>
        <SectionLabel orange={ORANGE}>What brings you to StrumIQ?</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {GOALS.map(g => (
            <Chip key={g} label={g} selected={goals.includes(g)} onToggle={() => toggleGoal(g)} orange={ORANGE} />
          ))}
        </div>
      </div>

      <button
        onClick={() => saveAndComplete(false)}
        disabled={step2Saving}
        style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: ORANGE, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 15, cursor: "pointer", marginBottom: 10 }}
      >
        {step2Saving ? "Saving…" : "Finish setup →"}
      </button>
      <button
        onClick={() => saveAndComplete(true)}
        disabled={step2Saving}
        style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1px solid ${BORDER}`, background: "transparent", color: DIM, fontFamily: "inherit", fontSize: 14, cursor: "pointer" }}
      >
        Skip for now
      </button>
    </div>
  );

  // ── Screen 3 — Add to Home Screen ─────────────────────────────────────────
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);

  const screen3 = (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "28px 24px 100px" }}>
      <ProgressDots total={3} current={2} orange={ORANGE} />
      <div style={{ fontSize: 26, fontWeight: 700, color: TEXT, marginBottom: 8 }}>Add StrumIQ to your Home Screen</div>
      <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 32 }}>
        Get the full app experience — works like a native app, no App Store needed.
      </div>

      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", marginBottom: 24 }}>
        {isIOS && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 12 }}>iOS Instructions</div>
            {[
              "Tap the Share button at the bottom of your browser",
              'Scroll down and tap "Add to Home Screen"',
              'Tap "Add" in the top right corner',
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: `rgba(${ORANGE === "#E8621A" ? "232,98,26" : "29,158,117"},0.15)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, fontWeight: 700, color: ORANGE }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{s}</div>
              </div>
            ))}
          </div>
        )}
        {isAndroid && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 12 }}>Android Instructions</div>
            {[
              "Tap the menu (⋮) in the top right of your browser",
              '"Add to Home Screen" or "Install App"',
              "Tap Install to confirm",
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: `rgba(${ORANGE === "#E8621A" ? "232,98,26" : "29,158,117"},0.15)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, fontWeight: 700, color: ORANGE }}>{i + 1}</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{s}</div>
              </div>
            ))}
          </div>
        )}
        {!isIOS && !isAndroid && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 8 }}>Desktop / Other</div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>Look for the install icon in your browser's address bar, or in the browser menu.</div>
          </div>
        )}
      </div>

      {deferredPrompt && (
        <button
          onClick={handlePWAInstall}
          style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: ORANGE, color: "#fff", fontFamily: "inherit", fontWeight: 700, fontSize: 15, cursor: "pointer", marginBottom: 10 }}
        >
          Install App
        </button>
      )}

      <button
        onClick={() => onComplete({ username: username.trim(), region, currency, platforms })}
        style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1px solid ${BORDER}`, background: "transparent", color: DIM, fontFamily: "inherit", fontSize: 14, cursor: "pointer" }}
      >
        Maybe later — go to the app
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: "100dvh", background: BG, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* Logo header */}
      <div style={{ padding: "20px 24px 0", display: "flex", justifyContent: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: ORANGE, letterSpacing: "0.1em", textTransform: "uppercase" }}>StrumIQ</div>
      </div>
      {step === 0 && screen1}
      {step === 1 && screen2}
      {step === 2 && screen3}
    </div>
  );
}
